<template>
	<div class="w-full">
		<GridLayout
			:sidebar-state="sidebarState"
			:reactive-sidebar="false"
		>
			<template #left-content>
				<FiltersSideBar
					ref="filtersSideBar"
					:visible="sidebarState"
					:filters="schedulesFilters"
					:schedules="scheduling.schedules"
					:weekdays="weekdays"
					@close="sidebarState = false"
					@filters-changed="OnSchedulesFiltersChange"
					@apply-filters="OnApplySchedulesFilters"
				/>
			</template>
			<template #left-footer>
				<div class="w-full h-full flex justify-center items-center gap-3">
					<button
						class="btn btn-fixed-size"
						@click="OnClearSchedulesFilters"
					>
						LIMPAR
					</button>
					<button
						class="btn btn-fixed-size"
						@click="$refs.filtersSideBar?.ApplyFilters()"
					>
						APLICAR
					</button>
				</div>
			</template>
			<template #right-header>
				<div class="w-full h-full flex items-center gap-3">
					<button
						class="btn btn-rounded"
						@click="Previous"
					>
						<i class="fas fa-chevron-left"></i>
					</button>
					<Steps
						:steps-map="stepsMap"
						:current-step="currentStep"
						:sidebar-state="sidebarState"
						:custom-style="{
							paddingX: stepsPadding,
							fontSize: stepsFontSize,
						}"
						@select-step="
							currentStep = $event
							autoNav = false
						"
					/>
					<FavoriteRoute />
				</div>
			</template>
			<template #right-tool-bar>
				<Subtitle :value="subtitle" />

				<ToolBar v-if="showExternalToolbar">
					<div
						v-if="step === 'schedules'"
						class="w-full flex gap-8"
					>
						<div class="flex items-center py-1">
							<button
								class="btn btn-tool"
								:class="{
									'has-filters': hasActiveSchedulesFilters,
								}"
								@click="sidebarState = !sidebarState"
							>
								<i
									class="fa-filter mx-2 font-20"
									:class="hasActiveSchedulesFilters ? 'fas' : 'far'"
								></i>
							</button>
						</div>
					</div>
					<div
						v-else-if="showPaymentToolbar"
						class="w-full flex items-center justify-center gap-3"
					>
						<button
							id="payment-btn"
							class="btn btn-light btn-fixed-size px-4"
							type="button"
							@click="$refs.confirm && $refs.confirm.OpenModal('#payment-methods-modal')"
						>
							PAGAMENTO
						</button>
					</div>
				</ToolBar>
			</template>
			<template #right-content>
				<transition
					name="fade"
					mode="out-in"
				>
					<div v-if="step === 'pre'"></div>

					<Modules
						v-if="step === 'module'"
						class="w-full flex-1 flex flex-col py-4"
						type="strict"
						:module_id="scheduling.module_id"
						@next="WalkStep"
					/>

					<Modalities
						v-if="step === 'modality'"
						class="w-full flex-1 flex flex-col py-4"
						type="strict"
						:module_id="scheduling.module_id"
						:modality_id="scheduling.modality_id"
						@next="WalkStep"
					/>

					<MainContent v-if="step === 'patient'">
						<PatientAvatar :avatar="scheduling.patient?.avatar" />

						<PatientForm
							ref="patient_form"
							class="w-full flex-1 flex flex-col px-4"
							:origin="isUrgentAppointment ? 'urgent_appointment' : 'pre_appointment'"
							:patient="scheduling.patient"
							:with-query="true"
							@loaded="PatientFormLoaded"
							@next="WalkStep"
							@selected="scheduling.patient = $event"
						/>
					</MainContent>

					<Partnership
						v-if="step === 'partnership'"
						ref="partnership"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						@next="WalkStep"
						@partnership-change="selectedPartnershipId = $event"
						@on-load-partnerships="partnerships = $event"
					/>

					<Guide
						v-if="step === 'guide'"
						ref="guide"
						class="w-full flex-1 flex flex-col"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						:partnerships="partnerships"
						:billing="scheduling.billing"
						:indicator="scheduling.indicator"
						:patient="scheduling.patient"
						:schedule_type="schedule_type"
						:origin="scheduling.origin"
						@next="WalkStep"
						@billing-change="scheduling.billing = $event"
						@indicator-change="scheduling.indicator = $event"
					/>

					<Proceedings
						v-if="step === 'proceedings'"
						ref="proceedings"
						:modality_id="scheduling.modality_id"
						:partnership="scheduling.partnership"
						:proceedings="scheduling.proceedings"
						:schedule_type="schedule_type"
						@next="WalkStep"
					/>

					<MainContent v-if="step === 'schedules'">
						<Schedules
							ref="schedules"
							class="w-full flex-1 flex flex-col py-4"
							type="scheduling"
							:modality_id="scheduling.modality_id"
							:proceedings="scheduling.proceedings"
							:schedules="scheduling.schedules"
							@next="WalkStep"
						/>
					</MainContent>

					<AttendancePriority
						v-if="step === 'attendance_priority'"
						ref="attendance_priority"
						class="w-full flex-1 flex flex-col py-4"
						:classification="scheduling.classification"
						:billing="scheduling.billing"
						@next="WalkStep"
					/>

					<MainContent v-if="step === 'confirm'">
						<Confirm
							ref="confirm"
							class="w-full flex-1 flex flex-col py-4"
							type="scheduling"
							:module_id="scheduling.module_id"
							:modality_id="scheduling.modality_id"
							:partnership="scheduling.partnership"
							:proceedings="scheduling.proceedings"
							:time="scheduling.time"
							:patient="scheduling.patient"
							:classification="scheduling.classification"
							:billing="scheduling.billing"
							:place="scheduling.place"
							:origin="scheduling.origin"
							:indicator="scheduling.indicator"
							:print-statement="printStatement"
							:journey-id="scheduling.journey_id"
							@next="WalkStep"
						/>
					</MainContent>
				</transition>
			</template>
			<template #right-footer>
				<div class="w-full h-full flex items-center gap-3 justify-start">
					<button
						v-if="isReceptionFlow"
						class="btn btn-danger btn-fixed-size"
						:disabled="reception_flow_data?.is_finished"
						@click="CancelFlow()"
					>
						SUSPENDER
					</button>
				</div>
				<div class="w-full h-full flex items-center gap-3 justify-center">
					<button
						class="btn btn-fixed-size"
						:disabled="step !== 'patient'"
						@click="
							$refs.patient_form.ClearForm()
							scheduling.patient = null
						"
					>
						LIMPAR
					</button>
					<button
						v-if="isReceptionFlow"
						class="btn btn-fixed-size"
						:disabled="patientsList.length !== 1 || !reception_flow_data?.is_finished"
						@click="NewSchedulingToPatient"
					>
						NOVO AGENDAMENTO
					</button>
				</div>
				<div class="w-full h-full flex items-center gap-3 justify-end">
					<button
						class="btn btn-success btn-fixed-size"
						type="button"
						:disabled="continueButtonDisabled && !isReceptionFlow"
						@click="Continue"
					>
						{{ finishButtonLabel }}
					</button>
				</div>
			</template>
		</GridLayout>
	</div>
</template>

<script>
	import _ from 'lodash'
	import { mapGetters, mapActions } from 'vuex'
	import Swal from 'sweetalert2'
	import { useError, useConfirmation } from '@/composables'
	import receptionFlowMixin from '@/mixins/receptionFlow'
	import { SCHEDULE_TYPES } from '@/constants'

	import Modules from '@/components/shared/Modules'
	import Modalities from '@/components/shared/Modalities'
	import Proceedings from '@/components/shared/scheduling/Proceedings'
	import Partnership from '@/components/shared/scheduling/Partnership'
	import Guide from '@/components/shared/scheduling/Guide'
	import Schedules from '@/components/shared/scheduling/Schedules'
	import Patient from '@/components/shared/scheduling/Patient'
	import AttendancePriority from '@/components/shared/scheduling/AttendancePriority'
	import Confirm from '@/components/shared/scheduling/Confirm'
	import FiltersSideBar from '@/components/shared/scheduling/FiltersSideBar'
	import PatientAvatar from '@/components/shared/patient/components/PatientAvatar'

	export default {
		name: 'SchedulingWizard',
		mixins: [receptionFlowMixin],
		components: {
			Modules,
			Modalities,
			Partnership,
			Guide,
			Proceedings,
			Schedules,
			Patient,
			AttendancePriority,
			Confirm,
			FiltersSideBar,
			PatientAvatar,
		},
		data() {
			return {
				SCHEDULE_TYPES: {
					'pre-agendamento': 1,
					'pronto-atendimento': 2,
					agendamento: 3,
				},
				printStatement: false,
				places: [],
				patients: [],
				partnerships: [],
				schedule_type: null,
				scheduling: {
					module_id: null,
					modality_id: null,
					journey_id: null,
					schedules: [],
					partnership: null,
					billing: null,
					proceedings: [],
					time: null,
					patient: null,
					place: null,
					classification: 1,
					obs: '',
					indicator: null,
					payment_info: null,
					origin: 2, // ORIGIN_SCHEDULING_PRE_APPOINTMENT
					instructions: '',
				},
				is_flow: false,
				el_laudo: null,
				skips: {
					pre: false,
					module: false,
					modality: false,
					partnerships: false,
					proceedings: false,
					schedules: false,
					patient: false,
					attendance_priority: false,
				},
				newSchedulingFlow: false,
				sidebarState: false,
				showSearchBar: false,
				selectedPartnershipId: null,
				hasActiveSchedulesFilters: false,
				schedulesFilters: {
					weekdays: [],
					period: [1, 2, 3],
					date_init: '',
					date_end: '',
					schedules: [],
				},
				patientsList: [],
				weekdays: window.weekDays,
				currentStep: 0,
				stepsMap: [
					{
						key: 'pre',
					},
					{
						key: 'module',
						number: 1,
						label: 'MÓDULO',
						isValid: false,
					},
					{
						key: 'modality',
						number: 2,
						label: 'MODALIDADE',
						isValid: false,
					},
					{
						key: 'patient',
						number: 3,
						label: 'CADASTRO',
						isValid: false,
					},
					{
						key: 'partnership',
						number: 4,
						label: 'CONVÊNIO',
						isValid: false,
					},
					{
						key: 'guide',
						number: 5,
						label: 'GUIA',
						isValid: false,
					},
					{
						key: 'proceedings',
						number: 6,
						label: 'PROCEDIMENTOS',
						isValid: false,
					},
					{
						key: 'schedules',
						number: 7,
						label: 'AGENDA',
						isValid: false,
					},
					{
						key: 'confirm',
						number: 8,
						label: 'AGENDAR',
						isValid: false,
					},
				],
				finishButtonLabel: 'CONTINUAR',
				autoNav: false,
			}
		},
		computed: {
			...mapGetters('auth', ['requester_selected_object', 'requester_selected_id']),
			...mapGetters('system', ['showSidebar', 'modules', 'modalities']),
			step() {
				return this.stepsMap[this.currentStep].key
			},
			showPaymentToolbar() {
				return this.step === 'confirm' && this.schedule_type === SCHEDULE_TYPES.URGENT_APPOINTMENT
			},
			showExternalToolbar() {
				return this.step === 'schedules' || this.showPaymentToolbar
			},
			continueButtonDisabled() {
				switch (this.step) {
					case 'patient':
						return false
					case 'partnership':
						return !this.selectedPartnershipId
					case 'guide':
						return false
					case 'proceedings':
						return this.scheduling.proceedings.length === 0
					case 'attendance_priority':
					case 'confirm':
						return false
					default:
						return true
				}
			},
			isUrgentAppointment() {
				return this.schedule_type === SCHEDULE_TYPES.URGENT_APPOINTMENT
			},
			stepsFontSize() {
				let fontSize = '1rem'

				if (this.sidebarState) {
					fontSize = '0.74rem'

					if (this.isUrgentAppointment) fontSize = '0.65rem'
				}

				return fontSize
			},
			stepsPadding() {
				let padding = '1rem'

				if (this.sidebarState) {
					if (this.isUrgentAppointment) padding = '0.5rem'
				}

				return padding
			},
			subtitle() {
				let subtitle = 'MÓDULO'

				if (this.scheduling.modality_id) {
					const modality = this.modalities.find(m => m.id === this.scheduling.modality_id)
					if (modality) subtitle = modality.name
				} else if (this.scheduling.module_id) {
					const module = this.modules.find(m => m.id === this.scheduling.module_id)
					if (module) subtitle = module.name
				}

				return subtitle
			},
		},
		watch: {
			step: {
				handler(val) {
					let hash = ''

					switch (val) {
						case 'module':
							hash = '#modulo'
							this.scheduling.modality_id = null
							this.stepsMap[2].isValid = false
							break
						case 'modality':
							hash = '#modalidade'
							break
						case 'patient':
							hash = '#paciente'
							break
						case 'partnership':
							if (this.isReceptionFlow) {
								this.reception_flow_data.is_finished = false
								this.finishButtonLabel = 'CONTINUAR'
							}

							hash = '#convenio'
							break
						case 'guide':
							hash = '#guia'
							break
						case 'proceedings':
							hash = '#procedimentos'
							break
						case 'schedules':
							hash = '#horarios'
							break
						case 'attendance_priority':
							hash = '#prioridade-de-atendimento'
							break
						case 'confirm':
							hash = '#confirmacao'
							break
					}

					window.location.hash = hash
					this.sidebarState = val === 'schedules'
				},
				immediate: true,
			},
			$route(to) {
				switch (to.hash) {
					case '#modulo':
						this.currentStep = 1
						this.scheduling.modality_id = null
						this.stepsMap[2].isValid = false
						break
					case '#modalidade':
						this.currentStep = 2
						break
					case '#paciente':
						this.currentStep = 3
						break
					case '#convenio':
						this.currentStep = 4
						break
					case '#guia':
						this.currentStep = 5
						break
					case '#procedimentos':
						this.currentStep = 6
						break
					case '#horarios':
						this.currentStep = 7
						break
					case '#prioridade-de-atendimento':
						if (this.isUrgentAppointment) {
							this.currentStep = 8
						}
						break
					case '#confirmacao':
						if (this.isUrgentAppointment) {
							this.currentStep = 9
						} else {
							this.currentStep = 8
						}

						this.finishButtonLabel = 'AGENDAR'
						break
					default:
						this.currentStep = 0
				}
			},
		},
		methods: {
			...mapActions('system', ['StartLoading', 'FinishLoading', 'GetModulesAndModalities']),
			CheckSkip(step) {
				let is_skip = this.skips[step]

				if (!is_skip) return

				let param = null

				switch (step) {
					case 'module':
						param = {
							id: this.scheduling.module_id,
						}
						break
					case 'modality':
						param = {
							id: this.scheduling.modality_id,
						}
						break
					case 'patient':
						param = this.scheduling.patient
						break
					case 'partnership':
						break
					case 'guide':
						break
					case 'proceedings':
						break
					case 'schedule':
						if (this.isUrgentAppointment) {
							this.currentStep = 7
						} else {
							this.currentStep = 6
						}
						break
					case 'attendance_priority':
						if (this.isUrgentAppointment) {
							this.currentStep = 8
						} else {
							this.currentStep = 7
						}
						break
				}

				this.WalkStep(param)
			},
			Previous() {
				switch (this.step) {
					case 'module':
						this.$router.push({ name: 'Home' })
						break
					case 'modality':
					case 'patient':
					case 'partnership':
					case 'guide':
					case 'proceedings':
					case 'schedules':
					case 'attendance_priority':
					case 'confirm':
						this.currentStep--
						break
					default:
						this.$emit('previous')
				}

				this.autoNav = false
			},
			ResetSteps() {
				if (!this.skips.pre) {
					this.scheduling.patient = null
					this.scheduling.partnership = null
					this.scheduling.module_id = null
					this.scheduling.modality_id = null
					this.scheduling.proceedings = []
					this.scheduling.schedules = []
					this.scheduling.time = null
					this.scheduling.classification = 1
					this.scheduling.places = null
					this.scheduling.obs = ''
					this.scheduling.journey_id = null
					this.scheduling.indicator = null
					this.scheduling.payment_info = null
					this.scheduling.instructions = ''
					this.scheduling.billing = null
				}

				this.stepsMap = this.stepsMap.map(step => {
					return {
						...step,
						isValid: false,
					}
				})
			},
			async WalkStep(param = null) {
				this.autoNav = true

				switch (this.step) {
					case 'pre':
						this.ResetSteps()
						this.currentStep = 1
						break
					case 'module':
						this.scheduling.module_id = param.id

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'modality':
						this.scheduling.modality_id = param.id

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'patient':
						this.scheduling.patient = param.patient

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'partnership':
						this.scheduling.partnership = param.partnership

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'guide':
						this.scheduling.billing = param.billing
						this.scheduling.indicator = param.indicator

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'proceedings':
						this.scheduling.proceedings = param.proceedings
						this.scheduling.schedules = _.orderBy(param.schedules, 'nome')

						this.stepsMap[this.currentStep].isValid = this.scheduling.proceedings.length > 0
						this.currentStep++
						break
					case 'schedules':
						this.scheduling.time = param

						this.scheduling.origin = this.GetAttendanceOrigin()

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++

						// if (
						// 	this.newSchedulingFlow &&
						// 	this.scheduling?.patient?.id
						// ) {
						// 	this.step = 'confirm'
						// } else {
						// 	this.step = 'patient'
						// }
						break
					case 'attendance_priority':
						this.scheduling.classification = param.classification

						this.stepsMap[this.currentStep].isValid = true
						this.currentStep++
						break
					case 'confirm':
						this.scheduling.obs = param.obs
						this.scheduling.place = param.place
						this.scheduling.payment_info = param.payment_methods_info
						this.scheduling.instructions = param.instructions

						await this.SaveScheduling()
						break
				}

				this.CheckSkip(this.step)
			},
			Continue() {
				if (this.isReceptionFlow && this.reception_flow_data?.is_finished) {
					this.FinishFlow()
					return
				}

				const refMap = {
					patient: 'patient_form',
					partnership: 'partnership',
					guide: 'guide',
					proceedings: 'proceedings',
					attendance_priority: 'attendance_priority',
					confirm: 'confirm',
				}

				const ref = refMap[this.step]

				if (ref && this.$refs[ref]?.Continue) {
					this.$refs[ref].Continue()
				}
			},
			NormalizeSchedulesFilters(filters = {}) {
				return {
					weekdays: [...(filters.weekdays ?? [])],
					period: [...(filters.period ?? [1, 2, 3])],
					date_init: filters.date_init ?? '',
					date_end: filters.date_end ?? '',
					schedules: [...(filters.schedules ?? [])],
				}
			},
			HasActiveSchedulesFilters(filters = this.schedulesFilters) {
				const normalizedFilters = this.NormalizeSchedulesFilters(filters)

				return (
					normalizedFilters.weekdays.length > 0 ||
					normalizedFilters.schedules.length > 0 ||
					normalizedFilters.date_init !== '' ||
					normalizedFilters.date_end !== '' ||
					normalizedFilters.period.length !== 3
				)
			},
			OnSchedulesFiltersChange(filters) {
				this.hasActiveSchedulesFilters = this.HasActiveSchedulesFilters(filters)
			},
			OnClearSchedulesFilters() {
				const defaultFilters = this.NormalizeSchedulesFilters({
					weekdays: [],
					period: [1, 2, 3],
					date_init: '',
					date_end: '',
					schedules: [],
				})
				this.schedulesFilters = defaultFilters
				this.hasActiveSchedulesFilters = false
				this.$refs.schedules?.ApplyFilters(defaultFilters)
			},
			OnApplySchedulesFilters(filters) {
				const normalizedFilters = this.NormalizeSchedulesFilters(filters)

				this.schedulesFilters = normalizedFilters
				this.hasActiveSchedulesFilters = this.HasActiveSchedulesFilters(normalizedFilters)
				this.$refs.schedules?.ApplyFilters(normalizedFilters)
			},
			GetAttendanceOrigin() {
				const ORIGIN_SCHEDULING_PRE_APPOINTMENT = 2
				const ORIGIN_SCHEDULING_URGENT_APPOINTMENT = 3

				const SCHEDULING_CONTINOUS = 3

				let origin = ORIGIN_SCHEDULING_PRE_APPOINTMENT

				origin =
					this.scheduling.time.tipo === SCHEDULING_CONTINOUS
						? ORIGIN_SCHEDULING_URGENT_APPOINTMENT
						: ORIGIN_SCHEDULING_PRE_APPOINTMENT

				return origin
			},
			SaveScheduling() {
				this.StartLoading()

				delete this.scheduling.schedules

				return window.api
					.call('post', '/save-scheduling', {
						...this.scheduling,
						account_id: this.requester_selected_id,
					})
					.then(({ data }) => {
						if (!data.response) {
							if (data.errorType === 'insulfficient_time_on_the_block') {
								useError('NÃO HÁ TEMPO SUFICIENTE NA BLOCO SELECIONADO, SELECIONE OUTRO BLOCO.').then(
									() => {
										this.currentStep = 6
									}
								)
								return
							}

							useError('HOUVE UM ERRO AO CONCLUIR, CONTATE O SUPORTE')
							return
						}
						this.scheduling.journey_id = data.journey
						this.printStatement = true

						Swal.fire({
							icon: 'question',
							title: 'DESEJA IMPRIMIR OS DADOS DO AGENDAMENTO?',
							showConfirmButton: true,
							showCancelButton: true,
							confirmButtonText: 'SIM',
							cancelButtonText: 'NÃO',
							reverseButtons: true,
							customClass: {
								container: 'themed confirmation',
							},
						}).then(async res => {
							if (res.isConfirmed) {
								this.PrintTimeline(data.journey)
							}

							this.printStatement = false

							if (!this.is_flow) {
								this.finishButtonLabel = 'CONCLUIR'
								this.reception_flow_data.is_finished = true
								this.currentStep = 0
								this.WalkStep()
							}

							useConfirmation().then(() => {
								if (this.is_flow) {
									window.location.href = `${process.env.VUE_APP_APP_URL}/laudar/finalizar`
								}

								if (this.newSchedulingFlow) {
									this.NewSchedulingCallbackHandler()
								}
							})
						})
					})
					.finally(() => {
						this.FinishLoading()
					})
			},
			PrintTimeline() {
				const fn = event => {
					if (event.data.print) {
						window.removeEventListener('message', fn)

						win.close()
					}
				}

				const win = window.open(`/pesquisa/imprimir?journey_id=${this.scheduling.journey_id}`, '_blank')
				win.mainWindow = window
				window.addEventListener('message', fn)
			},
			SetIndicator(indicator) {
				this.scheduling.indicator = indicator
			},
			GetFlowData() {
				return window.api.call('post', '/get-flow-data').then(({ data }) => {
					if (data.response) {
						this.el_laudo = data.el_laudo

						this.scheduling.module_id = this.el_laudo.flowData.idModule
						this.scheduling.modality_id = this.el_laudo.flowData.idModality
						this.scheduling.patient = this.el_laudo.step_1.paciente
						this.scheduling.indicator = this.el_laudo.indicator

						this.skips['pre'] = true
						this.skips['module'] = true
						this.skips['modality'] = true
						this.skips['patient'] = true
					} else {
						Swal.fire({
							icon: 'error',
							title: 'Erro!',
							text: data.message ?? 'Ocorreu um erro, contate o suporte!',
							customClass: {
								container: 'themed',
							},
						})
					}
				})
			},
			async FlowHandler() {
				this.is_flow = this.$route?.query?.flow ?? false

				if (this.is_flow) {
					await this.GetFlowData()
				}
			},
			NewSchedulingFlowHandler() {
				this.newSchedulingFlow = this.$route?.query?.newSchedulingFlow === 'true' ? true : false

				const journey_id = this.$route?.query.journey_id ?? null

				this.GetJourneyDetails(journey_id)
			},
			GetJourneyDetails(journey_id = null) {
				if (!journey_id) return

				return window.api
					.call('post', '/get-journey-details', {
						organization_id: this.requester_selected_id,
						journey_id,
					})
					.then(async ({ status, data }) => {
						if (status !== 200) {
							useError(data.message)
							return
						}

						this.FillWithJourneyData(data)
					})
			},
			FillWithJourneyData({ module_id, modality_id, partnership_id, patient }) {
				this.scheduling.module_id = module_id
				this.scheduling.modality_id = modality_id
				this.scheduling.patient = patient

				this.currentStep = 2

				this.FillPartnership(partnership_id)
			},
			async FillPartnership(partnership_id) {
				await this.$nextTick()

				setTimeout(() => {
					this.$refs.partnerships.SelectPartnership(partnership_id)
				}, 500)
			},
			NewSchedulingCallbackHandler() {
				parent.postMessage(
					{
						type: 'newScheduling',
						finished: true,
					},
					'*'
				)
			},
			FixStepsMap() {
				if (this.isUrgentAppointment) {
					this.stepsMap.splice(8, 0, {
						key: 'attendance_priority',
						number: 8,
						label: 'PRIORIDADE',
						isValid: false,
					})

					this.stepsMap[9].number = 9
				}
			},
			NewSchedulingToPatient() {
				if (this.patientsList.length !== 1) return

				this.WalkStep({
					patient: this.patientsList[0],
					autoWalkStep: true,
				})

				if (this.isReceptionFlow) {
					this.reception_flow_data.is_finished = false
					this.finishButtonLabel = 'CONTINUAR'
				}
			},
			PatientFormLoaded() {
				if (this.isReceptionFlow && this.reception_flow_data.patient_id && !this.scheduling.patient) {
					this.$refs.patient_form.SelectPatient(parseInt(this.reception_flow_data.patient_id))
				}
			},
		},
		async beforeMount() {
			const path = this.$route.path.replace('/agenda/', '')

			this.schedule_type = this.SCHEDULE_TYPES[path]

			await this.HandleReceptionFlow()
			await this.FlowHandler()
			await this.GetModulesAndModalities({
				schedule_type: this.schedule_type,
				check_unavailable: true,
				with_instructions: true,
			})

			this.WalkStep()
			this.NewSchedulingFlowHandler()
			this.FixStepsMap()
		},
	}
</script>

<style lang="css" scoped>
	#payment-btn {
		height: 2.5rem;
	}
</style>
